import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import { emptyReleaseForm } from '../constants/forms';
import { getAuthToken, handleLogout as doLogout } from '../utils/auth';
import { formatDateTime } from '../utils/date';
import { authFetch } from '../services/api';
import { updateProfile, generateTelegramCode } from '../services/artist.service';
import { deleteAlbum, fetchAlbum } from '../services/album.service';
import { useToast } from '../contexts/ToastContext';
import { useUploadQueue } from '../contexts/UploadContext';

export const useDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePic, setProfilePic] = useState('');
  const [activeTab, setActiveTab] = useState('gallery');
  const [telegramCode, setTelegramCode] = useState('');
  const [telegramCodeExpiresAt, setTelegramCodeExpiresAt] = useState('');
  const [telegramCodeLoading, setTelegramCodeLoading] = useState(false);
  const [telegramCodeError, setTelegramCodeError] = useState('');
  const [telegramCodeCopied, setTelegramCodeCopied] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseMode, setReleaseMode] = useState('create');
  const [releaseSaving] = useState(false);
  const [releaseError, setReleaseError] = useState('');
  const [releaseForm, setReleaseForm] = useState(emptyReleaseForm);
  const [uploadError, setUploadError] = useState('');
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  const [quota, setQuota] = useState(null);

  const { uploads, startUpload } = useUploadQueue();

  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  const navigate = useNavigate();
  const { addToast } = useToast();

  const loadDashboard = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await authFetch('/api/artists/me', { signal: controller.signal });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.message || 'Impossible de charger les donnees du tableau de bord.');
        err.status = response.status;
        throw err;
      }
      const result = await response.json();

      if (controller.signal.aborted) return;

      setData(result);
      setProfilePic(result.artist.profile_picture_url || '');
    } catch (err) {
      if (controller.signal.aborted) return;

      if (err.status === 401 || String(err.message || '').includes('Session expirée') || String(err.message || '').includes('Non autorisé')) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(err.message || 'Impossible de charger les donnees du tableau de bord.');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [navigate]);

  const loadRevenueData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    setRevenueLoading(true);

    try {
      const response = await authFetch('/api/artists/revenue');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Impossible de charger les donnees de revenus.');
      }
      const result = await response.json();

      if (!mountedRef.current) return;

      setRevenueData(result);
    } catch (err) {
      if (!mountedRef.current) return;

      if (err.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      console.error('Error loading revenue data:', err);
    } finally {
      if (mountedRef.current) {
        setRevenueLoading(false);
      }
    }
  }, [navigate]);

  const loadQuota = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/status');
      if (res.ok) {
        const data = await res.json();
        if (data.system_enabled) setQuota(data);
      }
    } catch { /* silence */ }
  }, []);

  useEffect(() => {
    AOS.init({ duration: 600, once: true });
    const timer = setTimeout(() => {
      loadDashboard();
      loadRevenueData();
      loadQuota();
    }, 0);

    return () => {
      clearTimeout(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadDashboard, loadRevenueData]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowTelegramModal(false);
        setShowReleaseModal(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const handleUploadSuccess = () => {
      loadDashboard();
    };
    window.addEventListener('passio-upload-success', handleUploadSuccess);
    return () => window.removeEventListener('passio-upload-success', handleUploadSuccess);
  }, [loadDashboard]);

  const handleLogout = () => doLogout(navigate);

  const handleUpdateProfile = async (updates) => {
    try {
      const result = await updateProfile(updates);

      if (!mountedRef.current) return false;

      setData((prev) => {
        if (!prev) return prev;
        const newArtist = result?.artist || result;
        return { 
          ...prev, 
          artist: { ...prev.artist, ...(typeof newArtist === 'object' ? newArtist : {}), ...updates } 
        };
      });

      if (updates.profile_picture_url) {
        setProfilePic(updates.profile_picture_url);
      }

      return true;
    } catch (err) {
      if (!mountedRef.current) return false;

      if (err.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      throw err;
    }
  };

  const handleProfilePicUpload = async (file) => {
    if (!file) return;

    // Au lieu d'uploader directement, on ouvre le modal de rognage
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmCrop = async (blob) => {
    // Vérifier que le blob est valide
    if (!blob || blob.size === 0) {
      const msg = 'Erreur: impossible de générer l\'image. Veuillez réessayer.';
      setUploadError(msg);
      addToast(msg, 'error');
      setShowCropModal(false);
      setImageToCrop(null);
      return;
    }

    const formData = new FormData();
    formData.append('image', blob, 'profile.jpg');
    setIsUploadingProfilePic(true);
    setUploadError('');

    try {
      const uploadRes = await authFetch('/api/upload/profile-pic', {
        method: 'POST',
        body: formData
      });

      if (!uploadRes.ok) {
        const errorData = await uploadRes.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur serveur lors de l\'upload');
      }

      const uploadData = await uploadRes.json();
      
      if (!mountedRef.current) return;

      if (!uploadData || !uploadData.url) {
        throw new Error('URL de réponse manquante du serveur');
      }

      const updatedUrl = `${uploadData.url}?t=${Date.now()}`;
      
      // Mettre à jour l'affichage immédiatement
      setProfilePic(updatedUrl);
      
      // Sauvegarder en DB de manière synchrone (attendre la réponse)
      try {
        await handleUpdateProfile({ profile_picture_url: updatedUrl });
        addToast('Photo de profil mise à jour !', 'success');
      } catch {
        addToast('Photo uploadée, mais erreur de sauvegarde. Réessayez.', 'error');
        // On garde le modal ouvert pour permettre de réessayer
        setIsUploadingProfilePic(false);
        return;
      }
      
      // Succès complet: fermer le modal
      setShowCropModal(false);
      setImageToCrop(null);
      setIsUploadingProfilePic(false);
    } catch (err) {
      if (!mountedRef.current) return;
      const msg = err.message || 'Impossible d\'envoyer la photo.';
      setUploadError(msg);
      addToast(msg, 'error');
      setIsUploadingProfilePic(false);
    }
  };

  const openCreateRelease = () => {
    setReleaseMode('create');
    setReleaseError('');
    setReleaseForm({
      ...emptyReleaseForm,
      tracks: [{ id: Date.now(), title: '', file: null, preview: '' }]
    });
    setShowReleaseModal(true);
  };

  const openEditRelease = async (album) => {
    setReleaseMode('edit');
    setReleaseError('');
    setReleaseForm(emptyReleaseForm);
    setShowReleaseModal(true);

    try {
      const result = await fetchAlbum(album.id, true);

      // Map tracks from API format to form format
      const mappedTracks = (result.tracks || []).map((track, index) => ({
        id: track.id || `track-${index}`,
        title: track.title || '',
        duration: track.duration || null,
        position: track.position || index + 1,
        encrypted_audio_url: track.encrypted_audio_url || track.preview_url || null,
        preview_url: track.preview_url || track.encrypted_audio_url || null,
        audio_storage_provider: track.audio_storage_provider || null,
        audio_storage_key: track.audio_storage_key || null,
        lyrics_storage_key: track.lyrics_storage_key || null,
        lyrics_url: track.lyrics_url || null,
        has_lyrics: track.has_lyrics || false,
        // 🔵 media_file_id : réutilisation de fichier audio
        media_file_id: track.media_file_id || null,
        file: null,
        lyrics_file: null,
        preview: null
      }));

      setReleaseForm({
        id: result.id,
        title: result.title || '',
        slug: result.slug || '',
        description: result.description || '',
        price_ariary: result.price_ariary || '',
        status: result.status || 'published',
        publication_date: result.publication_date || new Date().toISOString(),
        type: result.type || 'album',
        is_free: !!result.is_free,
        cover_url: result.cover_url || '',
        cover_storage_provider: result.cover_storage_provider || '',
        cover_storage_key: result.cover_storage_key || '',
        cover_file: null,
        cover_preview: result.cover_url || '',
        tracks: mappedTracks
      });
    } catch (err) {
      setReleaseError(err.message || 'Impossible de charger la release.');
    }
  };

  const openViewRelease = async (album) => {
    setReleaseMode('view');
    setReleaseError('');
    // Set loading state first
    setReleaseForm({
      ...emptyReleaseForm,
      id: album.id,
      title: album.title || 'Chargement...',
      cover_url: album.cover_url || '',
      cover_preview: album.cover_url || ''
    });
    setShowReleaseModal(true);

    try {
      const result = await fetchAlbum(album.id, true);

      // Map tracks from API format to form format
      const mappedTracks = (result.tracks || []).map((track, index) => ({
        id: track.id || `track-${index}`,
        title: track.title || '',
        duration: track.duration || null,
        position: track.position || index + 1,
        encrypted_audio_url: track.encrypted_audio_url || track.preview_url || null,
        preview_url: track.preview_url || track.encrypted_audio_url || null,
        audio_storage_provider: track.audio_storage_provider || null,
        audio_storage_key: track.audio_storage_key || null,
        lyrics_storage_key: track.lyrics_storage_key || null,
        lyrics_url: track.lyrics_url || null,
        has_lyrics: track.has_lyrics || false,
        // 🔵 media_file_id : réutilisation de fichier audio
        media_file_id: track.media_file_id || null,
        file: null,
        lyrics_file: null,
        preview: null
      }));

      setReleaseForm({
        id: result.id,
        title: result.title || '',
        slug: result.slug || '',
        description: result.description || '',
        price_ariary: result.price_ariary || '',
        status: result.status || 'published',
        publication_date: result.publication_date || new Date().toISOString(),
        type: result.type || 'album',
        is_free: !!result.is_free,
        cover_url: result.cover_url || '',
        cover_storage_provider: result.cover_storage_provider || '',
        cover_storage_key: result.cover_storage_key || '',
        cover_file: null,
        cover_preview: result.cover_url || '',
        tracks: mappedTracks
      });
    } catch (err) {
      setReleaseError(err.message || 'Impossible de charger la release.');
    }
  };

  const closeReleaseModal = () => {
    setShowReleaseModal(false);
    setReleaseError('');
    setReleaseForm(emptyReleaseForm);
  };

  const handleGenerateTelegramCode = async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    setTelegramCodeLoading(true);
    setTelegramCodeError('');
    setTelegramCodeCopied(false);

    try {
      const codeData = await generateTelegramCode();

      if (!mountedRef.current) return;

      setTelegramCode(codeData.code);
      setTelegramCodeExpiresAt(codeData.expires_at);
    } catch (err) {
      if (!mountedRef.current) return;

      if (err.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setTelegramCodeError(err.message || 'Impossible de generer le code Telegram.');
    } finally {
      if (mountedRef.current) setTelegramCodeLoading(false);
    }
  };

  const handleCopyTelegramCode = async () => {
    if (!telegramCode) return;

    try {
      await navigator.clipboard.writeText(telegramCode);
      setTelegramCodeCopied(true);
      setTimeout(() => setTelegramCodeCopied(false), 2000);
    } catch {
      setTelegramCodeError('Impossible de copier le code automatiquement.');
    }
  };

  const handleTestTelegram = async () => {
    if (!data?.artist?.id) return;

    try {
      const response = await authFetch('/api/telegram/test-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artist_id: data.artist.id })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du test.');
      }

      addToast('Notification de test envoyée ! Vérifiez votre Telegram.', 'success');
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleReleaseFieldChange = (field, value) => {
    setReleaseForm((prev) => {
      const newData = { ...prev, [field]: value };

      // Business logic: Single can only have 1 track
      if (field === 'type' && value === 'single') {
        if (newData.tracks.length > 1) {
          newData.tracks = [newData.tracks[0]];
        }
        // Matrice des droits : artiste Free ne peut pas créer de single payant
        const isPremium = data?.artist?.subscription_tier === 'premium';
        if (!isPremium) {
          newData.is_free = true;
          newData.price_ariary = 0;
        }
      }

      // Business logic: Free pricing
      if (field === 'is_free' && value === true) {
        newData.price_ariary = 0;
      }

      // Reset is_free if type changes to album
      if (field === 'type' && value === 'album') {
        newData.is_free = false;
      }

      // Sync track title if type is single
      if (newData.type === 'single' && newData.tracks.length > 0) {
        newData.tracks[0].title = newData.title;
      }

      // Auto-change status based on publication date
      if (field === 'publication_date' && value) {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
          newData.status = 'scheduled';
        } else {
          newData.status = 'published';
        }
      }

      return newData;
    });
  };

  const [coverRatioWarning, setCoverRatioWarning] = useState('');

  const handleCoverChange = (file) => {
    if (!file) return;

    // Vérifier les dimensions avec un Image element
    const img = new Image();
    const preview = URL.createObjectURL(file);

    img.onload = () => {
      const tolerance = 0.05; // 5% de tolérance
      const ratio = img.width / img.height;
      const isSquare = Math.abs(ratio - 1) <= tolerance;

      if (!isSquare) {
        // Rejeter l'image — elle n'est pas carrée
        URL.revokeObjectURL(preview);
        const msg = `Image ${img.width}×${img.height} — seules les images carrées (1:1) sont acceptées.`;
        setCoverRatioWarning(msg);
        addToast(msg, 'error');
        setTimeout(() => setCoverRatioWarning(''), 6000);
        return; // Ne pas mettre à jour le formulaire
      }

      // Image carrée acceptée
      setCoverRatioWarning('');
      setReleaseForm((prev) => ({
        ...prev,
        cover_file: file,
        cover_preview: preview
      }));
    };

    img.onerror = () => {
      // Si erreur de chargement, on ne bloque pas l'upload
      URL.revokeObjectURL(preview);
      setCoverRatioWarning('');
      addToast('Impossible de lire l\'image. Veuillez réessayer.', 'error');
    };

    img.src = preview;
  };

  const getAudioDuration = (file) => {
    return new Promise((resolve) => {
      if (!file) { resolve(null); return; }
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.src = url;
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(Math.round(audio.duration));
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
    });
  };

  const handleTrackChange = async (index, field, value) => {
    let duration = null;
    if (field === 'file' && value) {
      duration = await getAudioDuration(value);
    }

    setReleaseForm((prev) => {
      const tracks = [...prev.tracks];
      
      // Auto-populate title from filename if title is empty
      let autoTitle = null;
      if (field === 'file' && value) {
        const nameWithoutExt = value.name.replace(/\.[^.]+$/, '');
        const currentTitle = tracks[index]?.title?.trim() || '';
        if (!currentTitle) {
          autoTitle = nameWithoutExt;
        }
      }

      tracks[index] = {
        ...(tracks[index] || { id: Date.now(), title: prev.type === 'single' ? prev.title : '', file: null, preview: '', duration: null, lyrics_file: null }),
        [field]: value,
        ...(autoTitle ? { title: autoTitle } : {})
      };
      
      if (prev.type === 'single' && field !== 'title') {
        tracks[index].title = prev.title;
      }

      if (duration !== null) {
        tracks[index].duration = duration;
      }
      return { ...prev, tracks };
    });
  };

  const moveTrack = (fromIndex, toIndex) => {
    setReleaseForm((prev) => {
      const tracks = [...prev.tracks];
      const [movedTrack] = tracks.splice(fromIndex, 1);
      tracks.splice(toIndex, 0, movedTrack);
      return { ...prev, tracks };
    });
  };

  const addTrackRow = () => {
    setReleaseForm((prev) => {
      const newTracks = [...prev.tracks, { id: Date.now(), title: '', file: null, preview: '' }];
      return { ...prev, tracks: newTracks };
    });
  };

  const handleDropAudioFiles = async (files) => {
    // Filter only audio files
    const audioExts = ['.mp3', '.wav', '.flac', '.ogg', '.aac', '.m4a', '.wma', '.opus', '.alac', '.aiff'];
    const audioFiles = Array.from(files).filter(file => {
      if (file.type?.startsWith('audio/')) return true;
      const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
      return audioExts.includes(ext);
    });

    if (audioFiles.length === 0) return;

    // Process each file to get duration
    for (let i = 0; i < audioFiles.length; i++) {
      const file = audioFiles[i];
      const duration = await getAudioDuration(file);

      setReleaseForm((prev) => {
        const tracks = [...prev.tracks];
        const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');

        if (prev.type === 'single') {
          // For single: set file on the existing first track
          tracks[0] = {
            ...(tracks[0] || { id: Date.now(), title: prev.title, file: null, preview: '', duration: null, lyrics_file: null }),
            file: file,
            title: prev.title,
            duration: duration || tracks[0]?.duration,
          };
        } else {
          // For album: add a new track per file
          const newTrack = {
            id: Date.now() + i + '_' + Math.random().toString(36).slice(2, 6),
            title: nameWithoutExt,
            file: file,
            preview: '',
            duration: duration,
            lyrics_file: null,
          };
          tracks.push(newTrack);
        }

        return { ...prev, tracks };
      });
    }
  };

  const removeTrackRow = (index) => {
    setReleaseForm((prev) => ({
      ...prev,
      tracks: prev.tracks.filter((_, currentIndex) => currentIndex !== index)
    }));
  };

  const handleSaveRelease = async (event) => {
    if (event) event.preventDefault();
    
    // Validations avant de commencer
    if (releaseMode === 'create' && !releaseForm.cover_file && !releaseForm.cover_url) {
      setReleaseError('Ajoute une pochette avant de creer la release.');
      return;
    }

    // Valider le prix minimum : 1000 Ar pour les releases payantes
    if (!releaseForm.is_free) {
      const price = Number(releaseForm.price_ariary);
      if (!price || price < 1000) {
        const errorMsg = 'Le prix minimum pour une release est de 1 000 Ar.';
        setReleaseError(errorMsg);
        addToast(errorMsg, 'error');
        return;
      }
    }

    // Valider que chaque piste a un fichier audio
    const tracksMissingAudio = [];
    releaseForm.tracks.forEach((track, index) => {
      const hasNewAudio = !!track.file;
      const hasExistingAudio = !!track.encrypted_audio_url;
      if (!hasNewAudio && !hasExistingAudio) {
        const trackLabel = track.title?.trim() 
          ? `Piste ${index + 1} — "${track.title.trim()}"` 
          : `Piste ${index + 1}`;
        tracksMissingAudio.push(trackLabel);
      }
    });

    if (tracksMissingAudio.length > 0) {
      const errorMsg = tracksMissingAudio.length === 1
        ? `${tracksMissingAudio[0]} : ajoute un fichier audio avant de publier.`
        : `Fichiers audio manquants : ${tracksMissingAudio.join(', ')}`;
      setReleaseError(errorMsg);
      addToast(errorMsg, 'error');
      return;
    }

    const artistName = data?.artist?.name || 'Artiste';

    try {
      // Close modal and delegate upload
      closeReleaseModal();
      await startUpload(releaseForm, releaseMode, artistName);
    } catch (err) {
      setReleaseError(err.message || 'Erreur lors du lancement du téléversement.');
      addToast(err.message || 'Erreur lors du lancement du téléversement.', 'error');
    }
  };

  const handleDeleteRelease = async (album) => {
    // La confirmation est gérée en amont via useModal dans les composants appelants

    try {
      await deleteAlbum(album.id);

      if (!mountedRef.current) return;

      await loadDashboard();
      addToast('Release supprimée.', 'success');
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message || 'Impossible de supprimer la release.');
      addToast('Impossible de supprimer la release.', 'error');
    }
  };

  // Derive upload state from background queue
  const creationProgress = {};
  const optimisticAlbums = [];

  uploads.forEach(upload => {
    if (upload.status === 'uploading' || upload.status === 'finalizing') {
      if (upload.mode === 'create') {
        optimisticAlbums.push({
          id: upload.id,
          title: upload.title,
          cover_url: upload.coverPreview,
          status: 'draft',
          type: 'album',
          isOptimistic: true
        });
        creationProgress[upload.id] = upload.progress;
      } else if (upload.mode === 'edit' && upload.albumId) {
        creationProgress[upload.albumId] = upload.progress;
      }
    }
  });

  const enrichedAlbums = data?.albums
    ? [
        ...data.albums.map(album => {
          const isUploading = uploads.some(u => u.albumId === album.id && u.status === 'uploading');
          return {
            ...album,
            isOptimistic: isUploading ? true : album.isOptimistic
          };
        }),
        ...optimisticAlbums
      ]
    : null;

  const enrichedData = data
    ? { ...data, albums: enrichedAlbums }
    : null;

  return {
    data: enrichedData,
    loading,
    error,
    profilePic,
    activeTab,
    telegramCode,
    telegramCodeExpiresAt,
    telegramCodeLoading,
    telegramCodeError,
    telegramCodeCopied,
    showTelegramModal,
    showReleaseModal,
    releaseMode,
    releaseSaving,
    releaseError,
    releaseForm,
    setActiveTab,
    setShowTelegramModal,
    setShowReleaseModal,
    handleLogout,
    handleUpdateProfile,
    handleProfilePicUpload,
    openCreateRelease,
    openEditRelease,
    openViewRelease,
    closeReleaseModal,
    handleGenerateTelegramCode,
    handleCopyTelegramCode,
    handleTestTelegram,
    handleReleaseFieldChange,
    handleCoverChange,
    handleTrackChange,
    moveTrack,
    addTrackRow,
    removeTrackRow,
    handleSaveRelease,
    handleDeleteRelease,
    handleDropAudioFiles,
    formatDateTime,
    loadDashboard,
    uploadError,
    setUploadError,
    creationProgress,
    isUploadingProfilePic,
    showCropModal,
    imageToCrop,
    setShowCropModal,
    handleConfirmCrop,
    revenueData,
    revenueLoading,
    loadRevenueData,
    loadQuota,
    quota,
    coverRatioWarning,
  };
};
